import { vercelEnvToSentryEnvironment } from "./sentry-environment";

/**
 * Identity of a single build: baked into the bundle at build time by
 * `vite.config.ts` (as `__BUILD_INFO__`) and published beside the deployment
 * as `version.json`. Comparing the two is what tells a running tab it has gone
 * stale — see [[build-stamp-chip.spec]].
 */
export type BuildInfo = {
  /**
   * Human-readable name of the change that produced this build — the first
   * line of the triggering commit message, which for our squash-merged PRs is
   * the PR title (`fix(autodesk): tidy project-settings connect picker (#289)`).
   *
   * This is the field a tester actually reads. A SHA identifies a build only
   * if you already know what that SHA contains; the title says it outright.
   * `null` off a git-triggered deploy (a local `vite build`).
   */
  label: string | null;
  /** Commit SHA — the identity compared to detect a stale bundle. */
  sha: string | null;
  /** Branch the deploy was triggered from. */
  ref: string | null;
  /** ISO-8601 instant the bundle was built. */
  builtAt: string;
  /**
   * `"prod"` on the live deployment, `"dev"` on every other deploy, `null` on
   * a local build. Derived from the same Vercel mapping the Sentry environment
   * label uses, so there is exactly one definition of "this is production".
   */
  env: "prod" | "dev" | null;
};

/**
 * Filename of the published build manifest, written to the root of the build
 * output and fetched back at runtime to detect a stale tab. Shared so the
 * emitting build step and the fetching client can never drift apart.
 */
export const BUILD_MANIFEST_FILENAME = "version.json";

/** Absolute path the client fetches the manifest from. */
export const BUILD_MANIFEST_PATH = `/${BUILD_MANIFEST_FILENAME}`;

/** Vercel build-time variables this module reads. All optional — absent locally. */
export type BuildEnv = {
  VERCEL_ENV?: string;
  VERCEL_GIT_COMMIT_SHA?: string;
  VERCEL_GIT_COMMIT_MESSAGE?: string;
  VERCEL_GIT_COMMIT_REF?: string;
};

/**
 * Upper bound on the baked-in label. Vercel truncates the commit message at
 * 2048 bytes; we only ever render one line of it, so there is no reason to
 * ship the rest to every client.
 */
const MAX_LABEL_LENGTH = 120;

/** Treats an unset or whitespace-only value as absent. */
function orNull(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Reduces a raw commit message to its subject line — the part that reads as a
 * change description. Squash merges put the PR title and number there and the
 * body below, so everything after the first newline is noise for this purpose.
 */
function toLabel(message: string | undefined): string | null {
  const subject = orNull(message?.split("\n")[0]);
  return subject ? subject.slice(0, MAX_LABEL_LENGTH) : null;
}

/**
 * Builds the {@link BuildInfo} for the bundle currently being compiled.
 *
 * `builtAt` is passed in rather than read from the clock here so the same
 * value can be baked into the bundle and written to `version.json` — if the
 * two disagreed, every fresh load would look stale to itself.
 */
export function resolveBuildInfo(env: BuildEnv, builtAt: string): BuildInfo {
  return {
    label: toLabel(env.VERCEL_GIT_COMMIT_MESSAGE),
    sha: orNull(env.VERCEL_GIT_COMMIT_SHA),
    ref: orNull(env.VERCEL_GIT_COMMIT_REF),
    builtAt,
    env: vercelEnvToSentryEnvironment(orNull(env.VERCEL_ENV) ?? undefined) ?? null,
  };
}

/**
 * Whether a build carries enough identity to be compared against a published
 * manifest. A local build has no SHA, so there is nothing to go stale against
 * and the chip stays inert rather than polling a manifest that will never exist.
 */
export function isComparableBuild(info: BuildInfo): boolean {
  return info.sha !== null;
}
